/**
 * @file CarrierCheckoutMapSelect.tsx
 * @description Выбор карты ПВЗ на витрине (решение пользователя 2026-08-14).
 *
 * У ключа Яндекса может не быть прав на геокодер — тогда виджет СДЭК на
 * витрине падает. Альтернатива — своя карта Leaflet+OSM из кэша ПВЗ.
 * Провайдер задаётся реестром `MAP_PROVIDER_OPTIONS`: новый вариант = запись,
 * без правок разметки.
 */

import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import type { CheckoutMapProvider } from "../../lib/api-shop-carriers";

const MAP_PROVIDER_OPTIONS: Array<{ value: CheckoutMapProvider; label: string }> = [
  { value: "AUTO", label: "Авто (Яндекс, при сбое — OSM)" },
  { value: "YANDEX", label: "Яндекс.Карты (виджет СДЭК)" },
  { value: "OSM", label: "OpenStreetMap (своя карта)" },
];

/** base-ui рендерит сырое value — подписи отдаём картой. */
const MAP_PROVIDER_LABELS: Record<string, string> = Object.fromEntries(
  MAP_PROVIDER_OPTIONS.map((o) => [o.value, o.label]),
);

interface Props {
  value: CheckoutMapProvider;
  onChange: (provider: CheckoutMapProvider) => void;
  disabled?: boolean;
}

export function CarrierCheckoutMapSelect({ value, onChange, disabled }: Props) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor="checkoutMapProvider">Карта пунктов выдачи на витрине</Label>
      <Select
        value={value}
        disabled={disabled}
        onValueChange={(next) => {
          const found = MAP_PROVIDER_OPTIONS.find((o) => o.value === String(next ?? "AUTO"));
          onChange(found?.value ?? "AUTO");
        }}
      >
        <SelectTrigger
          id="checkoutMapProvider"
          className="sm:max-w-md"
          aria-label="Карта пунктов выдачи на витрине"
        >
          <SelectValue labels={MAP_PROVIDER_LABELS} placeholder="Авто (Яндекс, при сбое — OSM)" />
        </SelectTrigger>
        <SelectContent>
          {MAP_PROVIDER_OPTIONS.map((o) => (
            <SelectItem key={o.value} value={o.value}>
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <p className="text-xs text-on-surface-variant">
        Авто: при нерабочем ключе Яндекса витрина автоматически покажет карту OpenStreetMap —
        выбор пункта не сломается.
      </p>
    </div>
  );
}
